import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const toBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }
  return fallback;
};

const normalizeNumberString = (value, fallback = '') => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const normalized = String(value).trim();
  return normalized;
};

const ActionsConvertPdfToDocx = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  initialScope,
  actionType = 'convertpdftodocx',
  titleOverride = null,
}) => {
  const { t } = useTranslation();
  const isProjectScope = initialScope === 'project';
  const defaultSourceMode = isProjectScope ? 'other' : 'current';
  const resolvedActionType = actionType || 'convertpdftodocx';
  const resolvedTitle = titleOverride || t('workflow.convertpdftodocx', 'Convert PDF to DOCX');
  const isHtmlAction = resolvedActionType === 'convertpdftohtml';
  const isPdfTextAction = resolvedActionType === 'convertpdfimagetopdftext';
  const targetSectionTitle = isHtmlAction
    ? t('pdf_to_html.target_title', 'Output Directory')
    : isPdfTextAction
      ? t('pdf_image_to_pdf_text.target_title', 'Target Text PDF')
    : t('pdf_to_docx.target_title', 'Target DOCX');
  const targetPathPlaceholder = isHtmlAction
    ? t('pdf_to_html.target_path_placeholder', '/html_debug')
    : isPdfTextAction
      ? t('pdf_image_to_pdf_text.target_path_placeholder', '/{filename}_text.pdf')
    : t('pdf_to_docx.target_path_placeholder', '/{filename}_ocr.docx');
  const targetPathHelp = isHtmlAction
    ? t(
        'pdf_to_html.target_path_help',
        'Output directory for the generated HTML pages. Use tags like {filename} for dynamic naming.'
      )
    : isPdfTextAction
      ? t(
          'pdf_image_to_pdf_text.target_path_help',
          'Output path for the generated searchable text PDF. Use tags like {filename} for dynamic naming.'
        )
    : t(
        'pdf_to_docx.target_path_help',
        'Output path for the generated DOCX file. Use tags like {filename} for dynamic naming.'
      );
  const targetPathRequiredError = isHtmlAction
    ? t('pdf_to_html.target_path_error_required', 'An output directory is required before saving.')
    : isPdfTextAction
      ? t('pdf_image_to_pdf_text.target_path_error_required', 'A target PDF path is required before saving.')
    : t('pdf_to_docx.target_path_error_required', 'A target DOCX path is required before saving.');

  const [sourceMode, setSourceMode] = useState(defaultSourceMode);
  const [sourceFilePath, setSourceFilePath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [renderDpi, setRenderDpi] = useState('200');
  const [maxPages, setMaxPages] = useState('');
  const [includePageImages, setIncludePageImages] = useState(false);
  const [saveDebugHtmlPages, setSaveDebugHtmlPages] = useState(false);
  const [skipIfPdfHasText, setSkipIfPdfHasText] = useState(true);
  const [autoOrientOcr, setAutoOrientOcr] = useState(false);
  const [sourcePathError, setSourcePathError] = useState('');
  const [targetPathError, setTargetPathError] = useState('');
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const sourceRef = useRef(null);
  const targetRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: resolvedActionType,
    onSaveStatusChange,
    customComparison: (initial) => {
      if (String(initial?.id || '').startsWith('temp_')) {
        return false;
      }

      const initialSourceMode = String(initial?.sourceMode || initial?.source_mode || defaultSourceMode);
      const initialSourceFilePath = String(initial?.sourceFilePath || initial?.sourcePath || '');
      const initialTargetPath = String(initial?.targetPath || initial?.target || '');
      const initialRenderDpi = normalizeNumberString(initial?.renderDpi ?? initial?.dpi, '200');
      const initialMaxPages = normalizeNumberString(initial?.maxPages ?? initial?.max_pages, '');
      const initialIncludePageImages = toBoolean(initial?.includePageImages ?? initial?.include_page_images, false);
      const initialSaveDebugHtmlPages = toBoolean(initial?.saveDebugHtmlPages ?? initial?.save_debug_html_pages, false);
      const initialSkipIfPdfHasText = toBoolean(initial?.skipIfPdfHasText ?? initial?.skip_if_pdf_has_text, true);
      const initialAutoOrientOcr = toBoolean(initial?.autoOrientOcr ?? initial?.auto_orient_ocr, false);

      return (
        initialSourceMode === sourceMode &&
        initialSourceFilePath === sourceFilePath &&
        initialTargetPath === targetPath &&
        initialRenderDpi === renderDpi &&
        initialMaxPages === maxPages &&
        initialIncludePageImages === includePageImages &&
        initialSaveDebugHtmlPages === saveDebugHtmlPages &&
        initialSkipIfPdfHasText === skipIfPdfHasText &&
        initialAutoOrientOcr === autoOrientOcr
      );
    },
    customSaveData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourceFilePath.trim() : '',
      targetPath: targetPath.trim(),
      renderDpi: renderDpi.trim(),
      maxPages: maxPages.trim(),
      includePageImages,
      saveDebugHtmlPages,
      ...(isPdfTextAction ? { skipIfPdfHasText, autoOrientOcr } : {}),
    }),
    customForceRunData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourceFilePath.trim() : '',
      targetPath: targetPath.trim(),
      renderDpi: renderDpi.trim(),
      maxPages: maxPages.trim(),
      includePageImages,
      saveDebugHtmlPages,
      ...(isPdfTextAction ? { skipIfPdfHasText, autoOrientOcr } : {}),
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse PDF to DOCX data JSON:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(String(parsedData.sourceMode || parsedData.source_mode || defaultSourceMode));
      setSourceFilePath(String(parsedData.sourceFilePath || parsedData.sourcePath || ''));
      setTargetPath(String(parsedData.targetPath || parsedData.target || ''));
      setRenderDpi(normalizeNumberString(parsedData.renderDpi ?? parsedData.dpi, '200') || '200');
      setMaxPages(normalizeNumberString(parsedData.maxPages ?? parsedData.max_pages, ''));
      setIncludePageImages(toBoolean(parsedData.includePageImages ?? parsedData.include_page_images, false));
      setSaveDebugHtmlPages(toBoolean(parsedData.saveDebugHtmlPages ?? parsedData.save_debug_html_pages, false));
      setSkipIfPdfHasText(toBoolean(parsedData.skipIfPdfHasText ?? parsedData.skip_if_pdf_has_text, true));
      setAutoOrientOcr(toBoolean(parsedData.autoOrientOcr ?? parsedData.auto_orient_ocr, false));
    },
    enableConditions: true,
    initialScope,
  });

  useEffect(() => {
    updateSaveDisabledState?.();
  }, [
    sourceMode,
    sourceFilePath,
    targetPath,
    renderDpi,
    maxPages,
    includePageImages,
    saveDebugHtmlPages,
    skipIfPdfHasText,
    autoOrientOcr,
    updateSaveDisabledState,
  ]);

  const sourceTagOptions = useMemo(
    () => [
      { key: 'filename', label: 'Filename', value: 'filename', description: 'Source filename without extension' },
      { key: 'file_extension', label: 'File Extension', value: 'file_extension', description: 'Source file extension without the dot' },
      { key: 'file_folder_path', label: 'File Folder Path', value: 'file_folder_path', description: 'Folder path of the source file' },
      { key: 'source_file_path', label: 'Source File Path', value: 'source_file_path', description: 'Absolute path to the source file' },
      { key: 'source_file_name', label: 'Source File Name', value: 'source_file_name', description: 'Source file name with extension' },
      { key: 'current_date', label: 'Current Date', value: 'current_date', description: 'Current date in YYYY-MM-DD format' },
      { key: 'current_time', label: 'Current Time', value: 'current_time', description: 'Current time in HH:MM:SS format' },
      { key: 'current_datetime', label: 'Current DateTime', value: 'current_datetime', description: 'Current date and time' },
      { key: 'projectid', label: 'Project ID', value: 'projectid', description: 'Current project identifier' },
      { key: 'project_title', label: 'Project Title', value: 'project_title', description: 'Current project title' },
      { key: 'project_description', label: 'Project Description', value: 'project_description', description: 'Current project description' },
    ],
    []
  );

  const { handleBrowseDirectory: handleBrowseSourceDirectory, FolderBrowserComponent: SourceFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setSourceFilePath(folderPath);
        setSourcePathError('');
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderClean = folderPath.replace(/[\\/]+$/, '');
        const separator = folderClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderClean}${separator}{filename}${isPdfTextAction ? '_text.pdf' : '_ocr.docx'}`);
        setTargetPathError('');
      }
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'source') {
      sourceRef.current?.insertTag(value);
    } else if (menuType === 'target') {
      targetRef.current?.insertTag(value);
    }
    handleMenuClose();
  }, [handleMenuClose, menuType]);

  const handleSave = useCallback(() => {
    let hasError = false;

    if (sourceMode === 'other' && !sourceFilePath.trim()) {
      setSourcePathError(
        t('pdf_to_docx.source_path_error_required', 'A source PDF path is required before saving.')
      );
      hasError = true;
    } else if (sourcePathError) {
      setSourcePathError('');
    }

    if (!targetPath.trim()) {
      setTargetPathError(targetPathRequiredError);
      hasError = true;
    } else if (targetPathError) {
      setTargetPathError('');
    }

    if (!hasError) {
      baseHandleSave();
    }
  }, [baseHandleSave, sourceFilePath, sourceMode, t, targetPath, sourcePathError, targetPathError, targetPathRequiredError]);

  const renderDpiValue = renderDpi.trim() || '200';
  const maxPagesValue = maxPages.trim();
  const sourcePathHelperText = sourcePathError
    ? sourcePathError
    : (restrictToUserFolder
        ? t('pdf_to_docx.source_path_help', 'Paths must stay within allowed folders: {allowed}', {
            allowed: allowedRoots?.length
              ? allowedRoots.join(', ')
              : (displayRoot || t('pdf_to_docx.source_path_root_label', 'your restricted root')),
          })
        : t('pdf_to_docx.source_path_help_any', 'Paths must be accessible to the server.'));

  return (
      <ActionSettingsLayout
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        title={resolvedTitle}
        isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <PictureAsPdfIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    SOURCE
                  </Box>
                  {t('pdf_to_docx.source_title', 'Source PDF')}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={sourceMode}
                  onChange={(event) => {
                    setSourceMode(event.target.value);
                    if (sourcePathError) {
                      setSourcePathError('');
                    }
                  }}
                >
                  <FormControlLabel
                    value="current"
                    disabled={isProjectScope}
                    control={<Radio size="small" sx={{ color: '#5757b0', '&.Mui-checked': { color: '#5757b0' } }} />}
                    label={t('pdf_to_docx.source_mode_current', 'Current Source')}
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio size="small" sx={{ color: '#5757b0', '&.Mui-checked': { color: '#5757b0' } }} />}
                    label={t('pdf_to_docx.source_mode_other', 'Other Path')}
                  />
                </RadioGroup>
              </FormControl>

              {sourceMode === 'current' && (
                <Typography variant="body2" color="text.secondary">
                  {t('pdf_to_docx.source_mode_current_help', 'The current event PDF will be converted automatically.')}
                </Typography>
              )}

              {sourceMode === 'other' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Box sx={{ flex: 1 }}>
                      <TagEditor
                        caseid={caseid}
                        ref={sourceRef}
                        value={sourceFilePath}
                        onChange={(value) => {
                          setSourceFilePath(value);
                          if (sourcePathError) {
                            setSourcePathError('');
                          }
                        }}
                        placeholder={t('pdf_to_docx.source_path_placeholder', 'Enter the source PDF path (tags allowed)')}
                        singleLine
                        tags={tags}
                        onTagClick={(event) => handleMenuOpen(event, 'source')}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<FolderOpenIcon />}
                      onClick={() => {
                        setSourceBrowseDisabled(true);
                        handleBrowseSourceDirectory();
                      }}
                      disabled={sourceBrowseDisabled}
                      sx={{
                        borderRadius: '0 8px 8px 0',
                        borderColor: '#5757b0',
                        color: '#5757b0',
                        textTransform: 'none',
                        ml: -0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                          borderColor: '#5757b0',
                        },
                      }}
                    >
                      {t('common.browse', 'Browse')}
                    </Button>
                  </Box>
                  <Typography variant="caption" color={sourcePathError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                    {sourcePathHelperText}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <DescriptionIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    OUTPUT
                  </Box>
                  {targetSectionTitle}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                caseid={caseid}
                ref={targetRef}
                value={targetPath}
                onChange={(value) => {
                  setTargetPath(value);
                  if (targetPathError) {
                    setTargetPathError('');
                  }
                }}
                placeholder={targetPathPlaceholder}
                singleLine
                tags={tags}
                onTagClick={(event) => handleMenuOpen(event, 'target')}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>

              <Typography variant="caption" color={targetPathError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                {targetPathError || targetPathHelp}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <ExpandMoreIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    {t('pdf_to_docx.processing_title', 'Processing Options')}
                  </Box>
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('pdf_to_docx.render_dpi_label', 'Render DPI')}
                    value={renderDpiValue}
                    onChange={(event) => setRenderDpi(event.target.value)}
                    inputProps={{ min: 72, max: 600, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('pdf_to_docx.max_pages_label', 'Max pages')}
                    value={maxPagesValue}
                    onChange={(event) => setMaxPages(event.target.value)}
                    inputProps={{ min: 1, step: 1 }}
                    placeholder={t('pdf_to_docx.max_pages_placeholder', 'Leave blank for all pages')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={includePageImages}
                        onChange={(event) => setIncludePageImages(event.target.checked)}
                      />
                    )}
                    label={t('pdf_to_docx.include_page_images', 'Embed rendered page images')}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  {isPdfTextAction && (
                    <>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={autoOrientOcr}
                            onChange={(event) => setAutoOrientOcr(event.target.checked)}
                          />
                        )}
                        label={t('pdf_image_to_pdf_text.auto_orient_ocr', 'Auto-orient pages with OCR')}
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                        {t(
                          'pdf_image_to_pdf_text.auto_orient_ocr_help',
                          'Detects rotated PDF pages with OCR before generating page images for the vision model.'
                        )}
                      </Typography>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={skipIfPdfHasText}
                            onChange={(event) => setSkipIfPdfHasText(event.target.checked)}
                          />
                        )}
                        label={t('pdf_image_to_pdf_text.skip_if_pdf_has_text', 'Skip when source PDF already has text')}
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                        {t(
                          'pdf_image_to_pdf_text.skip_if_pdf_has_text_help',
                          'When enabled, the action only checks whether text can be extracted from the source PDF and skips OCR if text exists.'
                        )}
                      </Typography>
                    </>
                  )}
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={saveDebugHtmlPages}
                        onChange={(event) => setSaveDebugHtmlPages(event.target.checked)}
                      />
                    )}
                    label={t('pdf_to_docx.save_debug_html_pages', 'Save debug HTML pages to server tmp')}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5 }}>
                    {t(
                      'pdf_to_docx.save_debug_html_pages_help',
                      'Writes one HTML file and matching JSON debug payload per page under serverv2/tmp/pdf_to_docx_html/ for debugging and inspection.'
                    )}
                  </Typography>
                </Grid>
              </Grid>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {SourceFolderBrowser}
      {TargetFolderBrowser}

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        menuType={menuType}
        additionalTags={sourceTagOptions}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsConvertPdfToDocx);
